import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import dpiaRequestsApi from 'config/api/dpiaRequests/dpiaRequests';
import type { DpiaRequestOpenResponse, OpenDpiaRequestInput } from 'config/api/dpiaRequests/dpiaRequests.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useUnsavedChangesProtection from 'hooks/useUnsavedChangesProtection/useUnsavedChangesProtection';

import { mapFormValuesToDraftInput, mapRequestToFormValues, type DpiaNeedAssessmentFormValues } from './dpiaNeedAssessment.helpers';

type Params = {
  apaId: number;
  onCancelSuccess?: () => void;
  onDecideSuccess?: () => void;
  onNavigate: () => void;
  onSaveSuccess?: () => void;
  openInput?: OpenDpiaRequestInput;
  organizationId?: number;
};

const useDpiaNeedAssessment = ({
  apaId,
  onCancelSuccess,
  onDecideSuccess,
  onNavigate,
  onSaveSuccess,
  openInput,
  organizationId,
}: Params) => {
  const queryClient = useQueryClient();
  const [formValues, setFormValues] = useState<DpiaNeedAssessmentFormValues | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const openQuery = useQuery(
    [
      QUERY_KEYS.DPIA_REQUEST_OPEN,
      organizationId,
      apaId,
      openInput?.sourceRopaReviewRoundId,
      openInput?.sourceAssetProcessingActivityReviewId,
    ],
    async () => {
      const { data } = await dpiaRequestsApi.openRequest(organizationId as number, apaId, openInput);
      return data;
    },
    {
      enabled: Boolean(organizationId && apaId),
      retry: false,
    },
  );

  useEffect(() => {
    if (openQuery.data?.request) {
      setFormValues(mapRequestToFormValues(openQuery.data.request));
      setIsDirty(false);
    }
  }, [openQuery.data?.request]);

  const updateQueryCache = useCallback(
    (next: DpiaRequestOpenResponse) => {
      queryClient.setQueryData(
        [
          QUERY_KEYS.DPIA_REQUEST_OPEN,
          organizationId,
          apaId,
          openInput?.sourceRopaReviewRoundId,
          openInput?.sourceAssetProcessingActivityReviewId,
        ],
        next,
      );
      setFormValues(mapRequestToFormValues(next.request));
    },
    [apaId, openInput?.sourceAssetProcessingActivityReviewId, openInput?.sourceRopaReviewRoundId, organizationId, queryClient],
  );

  const saveMutation = useMutation(
    MUTATION_KEYS.DPIA_REQUEST_SAVE_DRAFT,
    async (values: DpiaNeedAssessmentFormValues) => {
      const requestId = openQuery.data?.request.id;
      if (!organizationId || !requestId) {
        throw new Error('Missing request context');
      }

      const { data } = await dpiaRequestsApi.saveDraft(organizationId, requestId, mapFormValuesToDraftInput(values));
      return data;
    },
    {
      onSuccess: savedRequest => {
        if (!openQuery.data) return;
        updateQueryCache({ ...openQuery.data, request: savedRequest });
        onSaveSuccess?.();
      },
    },
  );

  const decideRequiredMutation = useMutation(
    MUTATION_KEYS.DPIA_REQUEST_DECIDE_REQUIRED,
    async (values: DpiaNeedAssessmentFormValues) => {
      const requestId = openQuery.data?.request.id;
      if (!organizationId || !requestId || !values.decision) {
        throw new Error('Missing request context');
      }

      const { data } = await dpiaRequestsApi.decideRequired(organizationId, requestId, {
        ...mapFormValuesToDraftInput(values),
        decisionComment: values.decisionComment,
      });
      return data;
    },
    {
      onSuccess: data => {
        updateQueryCache(data);
        onDecideSuccess?.();
      },
    },
  );

  const decideNotRequiredMutation = useMutation(
    MUTATION_KEYS.DPIA_REQUEST_DECIDE_NOT_REQUIRED,
    async (values: DpiaNeedAssessmentFormValues) => {
      const requestId = openQuery.data?.request.id;
      if (!organizationId || !requestId || !values.decision) {
        throw new Error('Missing request context');
      }

      const { data } = await dpiaRequestsApi.decideNotRequired(organizationId, requestId, {
        ...mapFormValuesToDraftInput(values),
        decisionComment: values.decisionComment,
      });
      return data;
    },
    {
      onSuccess: data => {
        updateQueryCache(data);
        onDecideSuccess?.();
      },
    },
  );

  const cancelMutation = useMutation(
    MUTATION_KEYS.DPIA_REQUEST_CANCEL,
    async (cancellationComment: string) => {
      const requestId = openQuery.data?.request.id;
      if (!organizationId || !requestId) {
        throw new Error('Missing request context');
      }

      const { data } = await dpiaRequestsApi.cancelRequest(organizationId, requestId, { cancellationComment });
      return data;
    },
    {
      onSuccess: cancelledRequest => {
        if (!openQuery.data) return;
        updateQueryCache({ ...openQuery.data, request: cancelledRequest });
        onCancelSuccess?.();
      },
    },
  );

  const patchFormValues = useCallback((patch: Partial<DpiaNeedAssessmentFormValues>) => {
    setFormValues(current => (current ? { ...current, ...patch } : current));
    setIsDirty(true);
  }, []);

  const toggleCriterion = useCallback((code: DpiaNeedAssessmentFormValues['selectedCriteria'][number]) => {
    setFormValues(current => {
      if (!current) return current;

      const selected = current.selectedCriteria.includes(code)
        ? current.selectedCriteria.filter(item => item !== code)
        : [...current.selectedCriteria, code];

      return { ...current, selectedCriteria: selected };
    });
    setIsDirty(true);
  }, []);

  const handleSaveForNavigation = useCallback(async () => {
    if (!formValues) return;
    await saveMutation.mutateAsync(formValues);
    setIsDirty(false);
  }, [formValues, saveMutation]);

  const { allowNextNavigation, handleNavigation, NavigationPrompt } = useUnsavedChangesProtection({
    enabled: Boolean(formValues),
    isDirty,
    onNavigate,
    onSave: handleSaveForNavigation,
  });

  useEffect(() => {
    if (saveMutation.isSuccess) {
      setIsDirty(false);
    }
  }, [saveMutation.isSuccess]);

  const isDeciding = decideRequiredMutation.isLoading || decideNotRequiredMutation.isLoading;

  const request = openQuery.data?.request;
  const context = openQuery.data?.context;

  const { isLoading } = openQuery;
  const { isError } = openQuery;

  return useMemo(
    () => ({
      allowNextNavigation,
      cancelMutation,
      context,
      decideNotRequiredMutation,
      decideRequiredMutation,
      formValues,
      handleNavigation,
      isDeciding,
      isDirty,
      isError,
      isLoading,
      isSaving: saveMutation.isLoading,
      NavigationPrompt,
      patchFormValues,
      refetch: openQuery.refetch,
      request,
      saveMutation,
      toggleCriterion,
    }),
    [
      allowNextNavigation,
      cancelMutation,
      context,
      decideNotRequiredMutation,
      decideRequiredMutation,
      formValues,
      handleNavigation,
      isDeciding,
      isDirty,
      isError,
      isLoading,
      NavigationPrompt,
      openQuery.refetch,
      patchFormValues,
      request,
      saveMutation,
      toggleCriterion,
    ],
  );
};

export default useDpiaNeedAssessment;
